import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  Autocomplete,
  Table,
  TableContainer,
  TablePagination,
  ToggleButton,
  ToggleButtonGroup,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  Theme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import {
  BarChart,
  Bar,
  ErrorBar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';
import BusinessSwitcher from '../components/BusinessSwitcher';
import { CATEGORICAL_DARK, CATEGORICAL_LIGHT, NEUTRAL_DARK, NEUTRAL_LIGHT } from '../utils/chartPalette';
import { TableColumn, TableFilterRow, TableSearchField, useTableControls } from '../components/table/TableControls';
import { formatRate } from '../utils/bandRate';

interface Option {
  id: string;
  name: string;
}

// Listed in tree order by the API; depth 1 is a top group.
interface GroupOption extends Option {
  depth: number;
}

interface ActiveIngredientUsage {
  activeIngredientId: string;
  activeIngredientName: string;
  modeOfActionGroup: string | null;
  totalGramsActiveIngredient: number;
  totalAreaHectares: number;
  fieldAreaHectares: number;
  gramsPerHectare: number;
  effectiveGramsPerHectare: number;
}

interface RegionProductUsage {
  regionName: string;
  productId: string | null;
  productGroupId: string | null;
  productName: string;
  totalAreaHectares: number;
  fieldAreaHectares: number;
  averageRatePerHectare: number;
  medianRatePerHectare: number;
  minRatePerHectare: number;
  maxRatePerHectare: number;
  averageEffectiveRatePerHectare: number;
  medianEffectiveRatePerHectare: number;
  minEffectiveRatePerHectare: number;
  maxEffectiveRatePerHectare: number;
  rateUnitName: string;
  activeIngredients: ActiveIngredientUsage[];
}

interface ProductUsageReport {
  rows: RegionProductUsage[];
  skippedRecordProductCount: number;
  earliestRecordDate: string | null;
  latestRecordDate: string | null;
}

// What the charts show per ha. A band puts the application rate on part of
// the area, so 1 L/ha at a 10% band is an effective, broadacre 0.1 L/ha
// (utils/bandRate). With no bands in the selection the two are the same.
type RateBasis = 'application' | 'effective';

const NO_ROWS: RegionProductUsage[] = [];

const formatHa = (ha: number) => ha.toLocaleString('en-AU', { maximumFractionDigits: 1 });

// One row per region + product, with or without active ingredient data.
const rateColumns: TableColumn<RegionProductUsage>[] = [
  { key: 'region', label: 'Region', getValue: (r) => r.regionName, filter: 'select' },
  { key: 'product', label: 'Product', getValue: (r) => r.productName },
  { key: 'unit', label: 'Unit', getValue: (r) => r.rateUnitName, filter: 'select' },
  { key: 'wholeHa', label: 'Whole ha', getValue: (r) => formatHa(r.fieldAreaHectares), filter: 'none' },
  { key: 'treatedHa', label: 'Treated ha', getValue: (r) => formatHa(r.totalAreaHectares), filter: 'none' },
  { key: 'applicationAverage', label: 'Application rate, average', getValue: (r) => formatRate(r.averageRatePerHectare), filter: 'none' },
  { key: 'applicationMedian', label: 'Application rate, median', getValue: (r) => formatRate(r.medianRatePerHectare), filter: 'none' },
  { key: 'effectiveAverage', label: 'Effective rate, average', getValue: (r) => formatRate(r.averageEffectiveRatePerHectare), filter: 'none' },
  { key: 'effectiveMedian', label: 'Effective rate, median', getValue: (r) => formatRate(r.medianEffectiveRatePerHectare), filter: 'none' },
];

// Active ingredient per ha, g or kg: 2 decimals from 1 up, else 3
// significant digits — so a herbicide's 1.5 g/ha still reads 0.0015 kg/ha
// beside a fertiliser's hundreds of kg/ha rather than rounding to 0.
const formatAiAmount = (value: number): string =>
  value.toLocaleString('en-AU', Math.abs(value) >= 1 ? { maximumFractionDigits: 2 } : { maximumSignificantDigits: 3 });

// One grouped-bar-per-Region chart, reused for all three usage metrics
// (g ai/ha, average rate/ha, median rate/ha) — same axes/legend/tooltip
// shape throughout, only the data and value formatting differ per metric.
// showRange draws a min/max whisker per bar (see the rate charts below) —
// each data point must then also carry `${seriesName}__min`/`__max`
// alongside the bar's own value.
// One region's share of a total, for RegionShareDonut.
interface RegionShare {
  region: string;
  value: number;
}

// A region's share of a total (treated area, active ingredient) as a donut.
// The palette has 8 hues, so only the regions colorByRegion colours get
// their own slice; the rest share one grey "Other" slice. The list beside it
// still gives every region's %, largest first.
const RegionShareDonut: React.FC<{
  title: string;
  shares: RegionShare[];
  colorByRegion: Map<string, string>;
  neutral: string;
  formatValue: (value: number) => string;
  theme: Theme;
}> = ({ title, shares, colorByRegion, neutral, formatValue, theme }) => {
  const total = shares.reduce((sum, s) => sum + s.value, 0);
  const sorted = [...shares].filter((s) => s.value > 0).sort((a, b) => b.value - a.value);
  const coloured = sorted.filter((s) => colorByRegion.has(s.region));
  const others = sorted.filter((s) => !colorByRegion.has(s.region));
  const slices = [
    ...coloured.map((s) => ({ name: s.region, value: s.value, color: colorByRegion.get(s.region)! })),
    ...(others.length > 0
      ? [{ name: `Other (${others.length} region${others.length === 1 ? '' : 's'})`, value: others.reduce((sum, s) => sum + s.value, 0), color: neutral }]
      : []),
  ];
  const percent = (value: number) => (total > 0 ? `${((100 * value) / total).toFixed(value / total < 0.1 ? 1 : 0)}%` : '-');
  return (
    <Paper sx={{ p: 2, flex: '1 1 420px', minWidth: 0 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>{title}</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={slices} dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" stroke={theme.palette.background.paper} strokeWidth={2} isAnimationActive={false}>
                {slices.map((s) => <Cell key={s.name} fill={s.color} />)}
              </Pie>
              <Tooltip
                content={({ active, payload }) =>
                  active && payload && payload.length ? (
                    <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2"><strong>{payload[0].name}</strong></Typography>
                      <Typography variant="body2">{formatValue(Number(payload[0].value))} ({percent(Number(payload[0].value))})</Typography>
                    </Box>
                  ) : null
                }
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ flex: '1 1 180px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          {sorted.map((s) => (
            <Box key={s.region} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', flexShrink: 0, bgcolor: colorByRegion.get(s.region) ?? neutral }} />
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap title={s.region}>{s.region}</Typography>
              <Typography variant="body2" color="text.secondary">{percent(s.value)}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

interface UsageBarChartProps {
  title: string;
  data: Record<string, any>[];
  seriesNames: string[];
  colorFor: (index: number) => string;
  yAxisLabel: string;
  formatValue: (seriesName: string, value: number) => string;
  theme: Theme;
  showRange?: boolean;
}

// Every region labelled at 45°, smaller once there are many so they fit.
const regionAxisProps = (regionCount: number) =>
  ({ dataKey: 'region', interval: 0, angle: -45, textAnchor: 'end', height: 90, tick: { fontSize: regionCount > 10 ? 10 : 12 } }) as const;

const UsageBarChart: React.FC<UsageBarChartProps> = ({ title, data, seriesNames, colorFor, yAxisLabel, formatValue, theme, showRange }) => (
  <Paper sx={{ p: 2, flex: '1 1 420px', minWidth: 0 }}>
    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
      {title}
      {showRange && <Typography component="span" variant="caption" color="text.secondary"> (whiskers show min–max)</Typography>}
    </Typography>
    <ResponsiveContainer width="100%" height={440}>
      <BarChart data={data} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
        <XAxis {...regionAxisProps(data.length)} />
        <YAxis label={{ value: yAxisLabel, angle: -90, position: 'insideLeft' }} />
        <Tooltip
          content={({ active, payload, label }) => {
            if (active && payload && payload.length) {
              return (
                <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Typography variant="body2"><strong>{label}</strong></Typography>
                  {payload.map((p: any, i: number) => {
                    const min = showRange ? p.payload[`${p.name}__min`] : undefined;
                    const max = showRange ? p.payload[`${p.name}__max`] : undefined;
                    return (
                      <Typography key={i} variant="body2" sx={{ color: p.color }}>
                        {p.name}: {formatValue(p.name, Number(p.value))}
                        {min !== undefined && max !== undefined && ` (range ${formatValue(p.name, min)} – ${formatValue(p.name, max)})`}
                      </Typography>
                    );
                  })}
                </Box>
              );
            }
            return null;
          }}
        />
        {seriesNames.length > 1 && <Legend />}
        {seriesNames.map((name, i) => (
          <Bar key={name} dataKey={name} name={name} fill={colorFor(i)} radius={[4, 4, 0, 0]}>
            {showRange && (
              <ErrorBar
                dataKey={(entry: any) => [Math.max(0, entry[name] - entry[`${name}__min`]), Math.max(0, entry[`${name}__max`] - entry[name])]}
                width={4}
                strokeWidth={2}
                stroke={theme.palette.mode === 'dark' ? '#ffffff' : '#0b0b0b'}
              />
            )}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  </Paper>
);

const Reports: React.FC = () => {
  const auth = useAuth();
  const theme = useTheme();
  const { selectedBusinessId } = useBusiness();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [productSearch, setProductSearch] = useState('');
  const [productOptions, setProductOptions] = useState<Option[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Option[]>([]);
  const productSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [groupSearch, setGroupSearch] = useState('');
  const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<GroupOption[]>([]);
  // How many levels of the group tree the picker lists; 0 = all levels.
  const [groupDepth, setGroupDepth] = useState(1);
  const groupSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [timePeriodOptions, setTimePeriodOptions] = useState<Option[]>([]);
  const [selectedTimePeriodIds, setSelectedTimePeriodIds] = useState<string[]>([]);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [report, setReport] = useState<ProductUsageReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A product/group/timeframe picked under one business rarely means
  // anything under another — clear the selection whenever the business
  // changes rather than silently keeping a now out-of-scope filter.
  useEffect(() => {
    setSelectedProducts([]);
    setSelectedGroups([]);
    setSelectedTimePeriodIds([]);
  }, [selectedBusinessId]);

  // Timeframes actually used by this business (+ children), most recent
  // first — no search-as-you-type here since a business's own season list
  // is short, unlike the product catalog.
  useEffect(() => {
    if (!selectedBusinessId) {
      setTimePeriodOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const params = new URLSearchParams({ businessId: selectedBusinessId });
      const res = await fetch(`/api/reports/available-time-periods?${params.toString()}`, { headers: authHeaders() });
      if (!cancelled && res.ok) {
        const data: Option[] = await res.json();
        setTimePeriodOptions(data);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedBusinessId, authHeaders]);

  // Product search-as-you-type, scoped to what selectedBusinessId (+ its
  // child businesses) has actually recorded — same debounced pattern as
  // ImportedRowMatchWizard.tsx's product Autocomplete, but against
  // /api/reports/available-products instead of the global /api/products
  // catalog.
  useEffect(() => {
    if (productSearchDebounce.current) clearTimeout(productSearchDebounce.current);
    if (!selectedBusinessId) {
      setProductOptions([]);
      return;
    }
    let cancelled = false;
    productSearchDebounce.current = setTimeout(async () => {
      const params = new URLSearchParams({ businessId: selectedBusinessId });
      if (productSearch) params.set('search', productSearch);
      const res = await fetch(`/api/reports/available-products?${params.toString()}`, { headers: authHeaders() });
      if (!cancelled && res.ok) {
        const data: Option[] = await res.json();
        setProductOptions(data);
      }
    }, 350);
    return () => {
      cancelled = true;
      if (productSearchDebounce.current) clearTimeout(productSearchDebounce.current);
    };
  }, [productSearch, selectedBusinessId, authHeaders]);

  useEffect(() => {
    if (groupSearchDebounce.current) clearTimeout(groupSearchDebounce.current);
    if (!selectedBusinessId) {
      setGroupOptions([]);
      return;
    }
    let cancelled = false;
    groupSearchDebounce.current = setTimeout(async () => {
      const params = new URLSearchParams({ businessId: selectedBusinessId });
      if (groupSearch) params.set('search', groupSearch);
      if (groupDepth > 0) params.set('maxDepth', String(groupDepth));
      const res = await fetch(`/api/reports/available-product-groups?${params.toString()}`, { headers: authHeaders() });
      if (!cancelled && res.ok) {
        const data: GroupOption[] = await res.json();
        setGroupOptions(data);
      }
    }, 350);
    return () => {
      cancelled = true;
      if (groupSearchDebounce.current) clearTimeout(groupSearchDebounce.current);
    };
  }, [groupSearch, groupDepth, selectedBusinessId, authHeaders]);

  const hasSelection = selectedProducts.length > 0 || selectedGroups.length > 0;

  const loadReport = useCallback(async () => {
    if (!selectedBusinessId || !hasSelection) {
      setReport(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ businessId: selectedBusinessId });
      selectedProducts.forEach((p) => params.append('productIds', p.id));
      selectedGroups.forEach((g) => params.append('productGroupIds', g.id));
      selectedTimePeriodIds.forEach((id) => params.append('timePeriodIds', id));
      if (fromDate) params.append('from', new Date(fromDate).toISOString());
      if (toDate) params.append('to', new Date(toDate).toISOString());

      const res = await fetch(`/api/reports/product-usage-by-region?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load report');
      setReport(await res.json());
    } catch (e: any) {
      setError(e.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [selectedBusinessId, hasSelection, selectedProducts, selectedGroups, selectedTimePeriodIds, fromDate, toDate, authHeaders]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const [rateBasis, setRateBasis] = useState<RateBasis>('application');
  const basisLabel = rateBasis === 'application' ? 'Application' : 'Effective';
  const rateTable = useTableControls(report?.rows ?? NO_ROWS, rateColumns);

  const seriesNames = useMemo(() => {
    if (!report) return [] as string[];
    return Array.from(new Set(report.rows.map((r) => r.productName)));
  }, [report]);

  const palette = theme.palette.mode === 'dark' ? CATEGORICAL_DARK : CATEGORICAL_LIGHT;
  const colorFor = (index: number) => palette[index % palette.length];

  // Fertilisers run to hundreds of thousands of g/ha (e.g. 82% N at 400 kg/ha),
  // so once any product passes 1000 g/ha the chart and table switch to kg —
  // all together, never a mix of g and kg in one view.
  const aiInKg = useMemo(
    () => !!report?.rows.some((row) => row.activeIngredients.reduce((sum, ai) => sum + ai.gramsPerHectare, 0) > 1000),
    [report]
  );
  const aiUnit = aiInKg ? 'kg' : 'g';
  const aiDivisor = aiInKg ? 1000 : 1;

  const chartData = useMemo(() => {
    if (!report) return [] as Record<string, any>[];
    const byRegion = new Map<string, Record<string, any>>();
    for (const row of report.rows) {
      const bucket = byRegion.get(row.regionName) ?? { region: row.regionName };
      const gramsPerHa = row.activeIngredients.reduce(
        (sum, ai) => sum + (rateBasis === 'application' ? ai.gramsPerHectare : ai.effectiveGramsPerHectare), 0);
      bucket[row.productName] = (bucket[row.productName] ?? 0) + gramsPerHa / aiDivisor;
      byRegion.set(row.regionName, bucket);
    }
    return Array.from(byRegion.values());
  }, [report, aiDivisor, rateBasis]);

  // Same Region-keyed shape as chartData, but for the raw application rate
  // rather than active-ingredient content — populated even for a product
  // with no AI data at all (see ProductUsageReportService), so this chart
  // can show something the g-ai/ha chart can't for those products.
  const rateChartData = useMemo(() => {
    if (!report) return [] as Record<string, any>[];
    const byRegion = new Map<string, Record<string, any>>();
    for (const row of report.rows) {
      const bucket = byRegion.get(row.regionName) ?? { region: row.regionName };
      const application = rateBasis === 'application';
      bucket[row.productName] = application ? row.averageRatePerHectare : row.averageEffectiveRatePerHectare;
      bucket[`${row.productName}__min`] = application ? row.minRatePerHectare : row.minEffectiveRatePerHectare;
      bucket[`${row.productName}__max`] = application ? row.maxRatePerHectare : row.maxEffectiveRatePerHectare;
      byRegion.set(row.regionName, bucket);
    }
    return Array.from(byRegion.values());
  }, [report, rateBasis]);

  // Median of the individual application rates (not area-weighted) — "the
  // rate most events actually used," resistant to a few outlier-area fields
  // skewing the average chart above it. Same min/max whisker as the average
  // chart — it's the same underlying rate distribution either way.
  const medianRateChartData = useMemo(() => {
    if (!report) return [] as Record<string, any>[];
    const byRegion = new Map<string, Record<string, any>>();
    for (const row of report.rows) {
      const bucket = byRegion.get(row.regionName) ?? { region: row.regionName };
      const application = rateBasis === 'application';
      bucket[row.productName] = application ? row.medianRatePerHectare : row.medianEffectiveRatePerHectare;
      bucket[`${row.productName}__min`] = application ? row.minRatePerHectare : row.minEffectiveRatePerHectare;
      bucket[`${row.productName}__max`] = application ? row.maxRatePerHectare : row.maxEffectiveRatePerHectare;
      byRegion.set(row.regionName, bucket);
    }
    return Array.from(byRegion.values());
  }, [report, rateBasis]);

  // A product's rate unit is consistent across regions (it's a property of
  // the product, not the record) — one lookup, used by the rate charts'
  // tooltips to show the real unit per series instead of a bare number.
  const rateUnitByProduct = useMemo(() => {
    const map: Record<string, string> = {};
    report?.rows.forEach((row) => { map[row.productName] = row.rateUnitName; });
    return map;
  }, [report]);

  const totalAreaHectares = useMemo(() => {
    if (!report) return 0;
    return report.rows.reduce((sum, r) => sum + r.totalAreaHectares, 0);
  }, [report]);

  const wholeAreaHectares = useMemo(() => report?.rows.reduce((sum, r) => sum + r.fieldAreaHectares, 0) ?? 0, [report]);

  const regionCount = chartData.length;

  // Per region: treated area, and active ingredient applied (summed over
  // the selected products) — the donuts' shares.
  const areaByRegion = useMemo(() => {
    const totals = new Map<string, number>();
    report?.rows.forEach((row) => totals.set(row.regionName, (totals.get(row.regionName) ?? 0) + row.totalAreaHectares));
    return Array.from(totals, ([region, value]) => ({ region, value }));
  }, [report]);
  const aiByRegion = useMemo(() => {
    const totals = new Map<string, number>();
    report?.rows.forEach((row) =>
      totals.set(row.regionName, (totals.get(row.regionName) ?? 0) + row.activeIngredients.reduce((sum, ai) => sum + ai.totalGramsActiveIngredient, 0)));
    return Array.from(totals, ([region, value]) => ({ region, value }));
  }, [report]);
  // The 7 regions with the most treated area get a hue each — the same in
  // both donuts — and the rest go grey (an 8th hue would be one "Other").
  const regionColors = useMemo(() => {
    const map = new Map<string, string>();
    [...areaByRegion].sort((a, b) => b.value - a.value).slice(0, palette.length - 1)
      .forEach((r, i) => map.set(r.region, palette[i]));
    return map;
  }, [areaByRegion, palette]);
  const neutral = theme.palette.mode === 'dark' ? NEUTRAL_DARK : NEUTRAL_LIGHT;
  const totalAiGrams = aiByRegion.reduce((sum, r) => sum + r.value, 0);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Product Usage by Region</Typography>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <BusinessSwitcher />
        <Autocomplete
          multiple
          sx={{ minWidth: 260, flexGrow: 1 }}
          options={groupOptions}
          value={selectedGroups}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          getOptionLabel={(o) => o.name}
          onInputChange={(_, value) => setGroupSearch(value)}
          onChange={(_, value) => setSelectedGroups(value)}
          // Indented group names are long; let the list outgrow the box.
          slotProps={{ popper: { sx: { minWidth: 460 }, placement: 'bottom-start' } }}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              key={option.id}
              sx={{ pl: `${16 + (option.depth - 1) * 20}px !important`, fontWeight: option.depth === 1 && groupDepth !== 1 ? 600 : undefined }}
            >
              {option.name}
            </Box>
          )}
          renderInput={(params) => <TextField {...params} label="Product Groups" placeholder="Search groups…" size="small" />}
        />
        <FormControl sx={{ minWidth: 170 }} size="small">
          <InputLabel id="group-depth-label">Group depth</InputLabel>
          <Select
            labelId="group-depth-label"
            value={groupDepth}
            label="Group depth"
            onChange={(e) => setGroupDepth(Number(e.target.value))}
          >
            <MenuItem value={1}>Top groups only</MenuItem>
            <MenuItem value={2}>2 levels</MenuItem>
            <MenuItem value={3}>3 levels</MenuItem>
            <MenuItem value={0}>All levels</MenuItem>
          </Select>
        </FormControl>
        <Autocomplete
          multiple
          sx={{ minWidth: 260, flexGrow: 1 }}
          options={productOptions}
          value={selectedProducts}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          getOptionLabel={(o) => o.name}
          onInputChange={(_, value) => setProductSearch(value)}
          onChange={(_, value) => setSelectedProducts(value)}
          renderInput={(params) => <TextField {...params} label="Products" placeholder="Search products…" size="small" />}
        />
        <FormControl sx={{ minWidth: 220 }} size="small" disabled={timePeriodOptions.length === 0}>
          <InputLabel id="timeframe-label">Timeframe</InputLabel>
          <Select
            labelId="timeframe-label"
            multiple
            value={selectedTimePeriodIds}
            onChange={(e) => setSelectedTimePeriodIds(e.target.value as string[])}
            input={<OutlinedInput label="Timeframe" />}
            renderValue={(selected) =>
              selected.length === 0
                ? 'All timeframes'
                : timePeriodOptions.filter((o) => selected.includes(o.id)).map((o) => o.name).join(', ')
            }
          >
            {timePeriodOptions.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                <Checkbox checked={selectedTimePeriodIds.includes(option.id)} />
                <ListItemText primary={option.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          type="date"
          label="From"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          type="date"
          label="To"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {hasSelection && seriesNames.length > 8 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {seriesNames.length} products selected — narrow your selection to 8 or fewer for a readable chart (colors repeat past 8).
        </Alert>
      )}

      {!hasSelection ? (
        <Alert severity="info">Select one or more products or product groups above to see usage by region.</Alert>
      ) : loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      ) : !report || report.rows.length === 0 ? (
        <Alert severity="info">No matching records for this business and filter selection.</Alert>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mb: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Whole area</Typography>
              <Typography variant="h6" fontWeight={700}>{wholeAreaHectares.toFixed(1)} ha</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Treated area, after bands</Typography>
              <Typography variant="h6" fontWeight={700}>{totalAreaHectares.toFixed(1)} ha</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Regions</Typography>
              <Typography variant="h6" fontWeight={700}>{regionCount}</Typography>
            </Box>
            {report.earliestRecordDate && report.latestRecordDate && (
              <Box>
                <Typography variant="caption" color="text.secondary">Date range</Typography>
                <Typography variant="h6" fontWeight={700}>
                  {new Date(report.earliestRecordDate).toLocaleDateString()} – {new Date(report.latestRecordDate).toLocaleDateString()}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">Record-products skipped (unrecognized unit)</Typography>
              <Typography variant="h6" fontWeight={700}>{report.skippedRecordProductCount}</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <ToggleButtonGroup
              value={rateBasis}
              exclusive
              size="small"
              aria-label="Rate shown on the charts"
              onChange={(_, value) => value && setRateBasis(value)}
            >
              <ToggleButton value="application">Application rate</ToggleButton>
              <ToggleButton value="effective">Effective rate</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              {rateBasis === 'application'
                ? 'Per treated hectare, on the band: 1 L/ha at a 10% band shows as 1 L/ha.'
                : 'Per hectare of the whole area, after the band: 1 L/ha at a 10% band shows as 0.1 L/ha.'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <UsageBarChart
              title={`${basisLabel} ${aiUnit} active ingredient / ha`}
              data={chartData}
              seriesNames={seriesNames}
              colorFor={colorFor}
              yAxisLabel={`${aiUnit} active ingredient / ha`}
              formatValue={(_name, value) => `${formatAiAmount(value)} ${aiUnit}/ha`}
              theme={theme}
            />
            <UsageBarChart
              title={`${basisLabel} rate / ha, average`}
              data={rateChartData}
              seriesNames={seriesNames}
              colorFor={colorFor}
              yAxisLabel="rate / ha (native unit)"
              formatValue={(name, value) => `${value.toFixed(2)} ${rateUnitByProduct[name] || ''}`}
              theme={theme}
              showRange
            />
            <UsageBarChart
              title={`${basisLabel} rate / ha, median`}
              data={medianRateChartData}
              seriesNames={seriesNames}
              colorFor={colorFor}
              yAxisLabel="rate / ha (native unit)"
              formatValue={(name, value) => `${value.toFixed(2)} ${rateUnitByProduct[name] || ''}`}
              showRange
              theme={theme}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <RegionShareDonut
              title="Share of treated area by region"
              shares={areaByRegion}
              colorByRegion={regionColors}
              neutral={neutral}
              formatValue={(value) => `${value.toLocaleString('en-AU', { maximumFractionDigits: 1 })} ha`}
              theme={theme}
            />
            {totalAiGrams > 0 && (
              <RegionShareDonut
                title="Share of active ingredient applied by region"
                shares={aiByRegion}
                colorByRegion={regionColors}
                neutral={neutral}
                formatValue={(value) => `${formatAiAmount(value / (value >= 1000 ? 1000 : 1))} ${value >= 1000 ? 'kg' : 'g'}`}
                theme={theme}
              />
            )}
          </Box>

          <Box sx={{ mb: 1 }}>
            <TableSearchField value={rateTable.search} onChange={rateTable.setSearch} placeholder="Search rates…" />
          </Box>
          <Paper sx={{ mb: 3 }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {rateColumns.map((c) => (
                      <TableCell key={c.key} align={c.filter === 'none' ? 'right' : undefined}>{c.label}</TableCell>
                    ))}
                    <TableCell />
                  </TableRow>
                  <TableFilterRow columns={rateColumns} state={rateTable} />
                </TableHead>
                <TableBody>
                  {rateTable.filteredItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={rateColumns.length + 1} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No rates match.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {rateTable.pageItems.map((row) => (
                    <TableRow key={`${row.regionName}-${row.productId ?? row.productGroupId}`}>
                      {rateColumns.map((c) => (
                        <TableCell key={c.key} align={c.filter === 'none' ? 'right' : undefined}>{c.getValue(row)}</TableCell>
                      ))}
                      <TableCell />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" {...rateTable.paginationProps} />
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Region</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Active Ingredient</TableCell>
                  <TableCell align="right">Total {aiUnit} AI</TableCell>
                  <TableCell align="right">Whole ha</TableCell>
                  <TableCell align="right">Treated ha</TableCell>
                  <TableCell align="right">Application {aiUnit} AI / ha</TableCell>
                  <TableCell align="right">Effective {aiUnit} AI / ha</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.rows.flatMap((row) =>
                  row.activeIngredients.map((ai) => (
                    <TableRow key={`${row.regionName}-${row.productId ?? row.productGroupId}-${ai.activeIngredientId}`}>
                      <TableCell>{row.regionName}</TableCell>
                      <TableCell>{row.productName}</TableCell>
                      <TableCell>{ai.activeIngredientName}</TableCell>
                      <TableCell align="right">
                        {(ai.totalGramsActiveIngredient / aiDivisor).toLocaleString('en-AU', { maximumFractionDigits: 1 })}
                      </TableCell>
                      <TableCell align="right">{ai.fieldAreaHectares.toFixed(1)}</TableCell>
                      <TableCell align="right">{ai.totalAreaHectares.toFixed(1)}</TableCell>
                      <TableCell align="right">{formatAiAmount(ai.gramsPerHectare / aiDivisor)}</TableCell>
                      <TableCell align="right">{formatAiAmount(ai.effectiveGramsPerHectare / aiDivisor)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default Reports;
